import React, { useState, useEffect } from 'react';
import { Employee, ShiftAssignment, EmployeeStatus } from '../types';
import Modal from './Modal';

// Define InputField outside the EmployeeFormModal component - Enhanced for Touch
const InputField: React.FC<{
  label: string, 
  name: keyof Omit<Employee, 'id'>, // Ensure name is a key of Employee fields
  value: string, 
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
  error?: string, 
  required?: boolean
}> = ({ label, name, value, onChange, error, required }) => (
  <div className="mb-4">
    <label htmlFor={name} className="block text-sm font-medium text-slate-700 mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type="text"
      id={name}
      name={name}
      value={value}
      onChange={onChange}
      className={`w-full px-4 py-3 text-base border-2 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:bg-slate-50 transition-colors ${error ? 'border-red-500' : 'border-slate-300'}`}
      style={{ minHeight: '48px', fontSize: '16px' }}
      aria-required={required}
      aria-describedby={error ? `${name}-error` : undefined}
    />
    {error && <p id={`${name}-error`} className="mt-1 text-xs text-red-600">{error}</p>}
  </div>
);

interface EmployeeFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (employee: Omit<Employee, 'id'> | Employee, isNew: boolean) => void;
  initialData?: Employee | null;
  title: string;
}

const EmployeeFormModal: React.FC<EmployeeFormModalProps> = ({ isOpen, onClose, onSubmit, initialData, title }) => {
  const [formData, setFormData] = useState({
    employeeId: '',
    firstName: '',
    lastName: '',
    shiftAssignment: ShiftAssignment.SHIFT_1,
    status: EmployeeStatus.ACTIVE,
  });
  const [errors, setErrors] = useState({
    employeeId: '',
    firstName: '',
    lastName: '',
    shiftAssignment: '',
    status: '',
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        employeeId: initialData.employeeId,
        firstName: initialData.firstName,
        lastName: initialData.lastName,
        shiftAssignment: initialData.shiftAssignment,
        status: initialData.status,
      });
    } else {
      setFormData({ employeeId: '', firstName: '', lastName: '', shiftAssignment: ShiftAssignment.SHIFT_1, status: EmployeeStatus.ACTIVE });
    }
    setErrors({ employeeId: '', firstName: '', lastName: '', shiftAssignment: '', status: '' }); // Reset errors when modal opens/data changes
  }, [isOpen, initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' })); // Clear error on change
  };

  const validate = (): boolean => {
    let isValid = true;
    const newErrors = { employeeId: '', firstName: '', lastName: '', shiftAssignment: '', status: '' };

    if (!formData.employeeId.trim()) {
      newErrors.employeeId = 'Employee ID is required.';
      isValid = false;
    }
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First Name is required.';
      isValid = false;
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last Name is required.';
      isValid = false;
    }
    if (!formData.shiftAssignment) {
      newErrors.shiftAssignment = 'Shift Assignment is required.';
      isValid = false;
    }
    if (!formData.status) {
      newErrors.status = 'Status is required.';
      isValid = false;
    }
    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }
    if (initialData) {
      onSubmit({ ...initialData, ...formData }, false);
    } else {
      onSubmit(formData, true);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <form onSubmit={handleSubmit} noValidate>
        <InputField 
            label="Employee ID" 
            name="employeeId" 
            value={formData.employeeId} 
            onChange={handleChange} 
            error={errors.employeeId} 
            required 
        />
        <InputField 
            label="First Name" 
            name="firstName" 
            value={formData.firstName} 
            onChange={handleChange} 
            error={errors.firstName} 
            required 
        />
        <InputField 
            label="Last Name" 
            name="lastName" 
            value={formData.lastName} 
            onChange={handleChange} 
            error={errors.lastName} 
            required 
        />
        
        {/* Shift Assignment Field - Enhanced for Touch */}
        <div className="mb-4">
          <label htmlFor="shiftAssignment" className="block text-sm font-medium text-slate-700 mb-2">
            Shift Assignment <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <select
              id="shiftAssignment"
              name="shiftAssignment"
              value={formData.shiftAssignment}
              onChange={handleChange}
              className={`w-full px-4 py-3 pr-12 text-base font-medium border-2 rounded-lg shadow-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 appearance-none cursor-pointer bg-white hover:bg-indigo-50 hover:border-indigo-400 transition-all ${errors.shiftAssignment ? 'border-red-500 bg-red-50' : 'border-slate-300'}`}
              style={{ minHeight: '48px', fontSize: '16px' }}
              aria-required={true}
              aria-describedby={errors.shiftAssignment ? 'shiftAssignment-error' : undefined}
            >
              <option value={ShiftAssignment.SHIFT_1} className="py-2">Shift 1</option>
              <option value={ShiftAssignment.SHIFT_2} className="py-2">Shift 2</option>
              <option value={ShiftAssignment.BOTH_SHIFTS} className="py-2">Both Shifts</option>
            </select>
            {/* Dropdown Arrow Icon - Large and Prominent */}
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <div className="bg-slate-100 rounded-md p-1">
                <svg className="h-6 w-6 text-slate-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>
          {errors.shiftAssignment && (
            <p id="shiftAssignment-error" className="mt-1 text-xs text-red-600">
              {errors.shiftAssignment}
            </p>
          )}
        </div>

        {/* Employee Status Toggle - Enhanced for Touch */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Employee Status
          </label>
          <button
            type="button"
            onClick={() => {
              const newStatus = formData.status === EmployeeStatus.ACTIVE ? EmployeeStatus.SUSPENDED : EmployeeStatus.ACTIVE;
              setFormData(prev => ({ ...prev, status: newStatus }));
            }}
            className={`w-full px-6 py-4 text-base font-semibold rounded-lg border-2 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              formData.status === EmployeeStatus.ACTIVE
                ? 'bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-200 focus:ring-emerald-500'
                : 'bg-red-100 text-red-800 border-red-300 hover:bg-red-200 focus:ring-red-500'
            }`}
            style={{ minHeight: '48px' }}
          >
            <div className="flex items-center justify-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${
                formData.status === EmployeeStatus.ACTIVE ? 'bg-emerald-600' : 'bg-red-600'
              }`}></div>
              <span>
                {formData.status === EmployeeStatus.ACTIVE ? 'Active' : 'Suspended'}
              </span>
            </div>
          </button>
          <p className="text-xs text-slate-500 mt-1">
            Click to toggle between Active and Suspended status
          </p>
        </div>
        
        <div className="mt-6 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-3 text-base font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 border-2 border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
            style={{ minHeight: '48px' }}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-6 py-3 text-base font-medium text-white bg-indigo-600 hover:bg-indigo-700 border-2 border-transparent rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
            style={{ minHeight: '48px' }}
          >
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EmployeeFormModal;